import React, { useMemo, useState } from "react";

import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import ServerPaginatedTable from "@/Components/Common/ServerPaginatedTable";
import TableContainer from "@/Components/Common/TableContainer";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";

import {
  CRM_BADGE_COLORS,
  CRM_BUTTON_COLORS,
  CRM_SAMPLE_ROWS,
  CRM_SECTION_DESCRIPTIONS,
  CRM_SECTION_ORDER,
  CRM_TABLE_COLUMNS,
} from "./demoData";
import sharedUiMetadata from "./metadata.generated";

type SharedUiUsageEntry = {
  pageFile: string;
  routePaths: string[];
};

type SharedUiVariantMeta = {
  id: string;
  label: string;
  sourceFiles: string[];
  usages: SharedUiUsageEntry[];
};

type SharedUiComponentMeta = {
  id: string;
  label: string;
  sourceFiles: string[];
  usages: SharedUiUsageEntry[];
  variants?: SharedUiVariantMeta[];
};

const metadataById = new Map(
  (sharedUiMetadata as SharedUiComponentMeta[]).map((entry) => [entry.id, entry])
);

function RouteUsageBadge({ children }: { children: React.ReactNode }) {
  return (
    <Badge
      color="soft-primary"
      className="border-transparent bg-blue-100 text-blue-800 hover:bg-blue-100 hover:text-blue-800"
    >
      {children}
    </Badge>
  );
}

function RouteUsageCountBadge({ count }: { count: number }) {
  return (
    <Badge
      color="soft-dark"
      className="border-transparent bg-slate-800 text-white hover:bg-slate-800 hover:text-white"
    >
      {count} routed usages
    </Badge>
  );
}

function PathList({ title, paths }: { title: string; paths: string[] }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 text-sm font-semibold text-slate-800">{title}</div>
      <div className="space-y-2 text-xs text-slate-600">
        {paths.map((item) => (
          <div key={item} className="rounded-md bg-white px-3 py-2 font-mono">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function UsageList({ usages }: { usages: SharedUiUsageEntry[] }) {
  return (
    <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
      <Table responsive className="mb-0">
        <thead>
          <tr>
            <th>Page File</th>
            <th>Routes</th>
          </tr>
        </thead>
        <tbody>
          {usages.map((usage) => (
            <tr key={usage.pageFile}>
              <td className="align-top">
                <code>{usage.pageFile}</code>
              </td>
              <td className="align-top">
                <div className="flex flex-wrap gap-2">
                  {usage.routePaths.map((routePath) => (
                    <RouteUsageBadge key={routePath}>
                      {routePath}
                    </RouteUsageBadge>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function MetadataBlock({ meta }: { meta: SharedUiComponentMeta }) {
  return (
    <div className="space-y-4">
      <Row className="g-3">
        <Col lg={5}>
          <PathList title="Component File Locations" paths={meta.sourceFiles} />
        </Col>
        <Col lg={7}>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 text-sm font-semibold text-slate-800">
              Used Pages ({meta.usages.length})
            </div>
            <UsageList usages={meta.usages} />
          </div>
        </Col>
      </Row>
      {meta.variants?.length ? (
        <Row className="g-3">
          {meta.variants.map((variant) => (
            <Col lg={6} key={variant.id}>
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">{variant.label}</div>
                <div className="mb-3 space-y-2 text-xs text-slate-600">
                  {variant.sourceFiles.map((sourceFile) => (
                    <div key={sourceFile} className="rounded-md bg-slate-50 px-3 py-2 font-mono">
                      {sourceFile}
                    </div>
                  ))}
                </div>
                <UsageList usages={variant.usages} />
              </div>
            </Col>
          ))}
        </Row>
      ) : null}
    </div>
  );
}

function MockServerPaginatedTableDemo() {
  const pageSize = 15;
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(CRM_SAMPLE_ROWS.length / pageSize));
  const currentRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return CRM_SAMPLE_ROWS.slice(startIndex, startIndex + pageSize);
  }, [page]);

  const previous = page > 1 ? `/mock/crm-shared-ui?page=${page - 1}` : null;
  const next = page < totalPages ? `/mock/crm-shared-ui?page=${page + 1}` : null;

  const handleRequestData = (requestUrl: string) => {
    const searchParams = new URLSearchParams(requestUrl.split("?")[1] ?? "");
    const nextPage = Number(searchParams.get("page") || "1");
    if (Number.isFinite(nextPage) && nextPage >= 1 && nextPage <= totalPages) {
      setPage(nextPage);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="mb-3 text-sm text-slate-600">
        Local mock data with the same prop contract as the current shared server-style component.
      </div>
      <ServerPaginatedTable
        id="crm-shared-ui-server-table"
        url="/mock/crm-shared-ui"
        columns={CRM_TABLE_COLUMNS}
        data={currentRows}
        previous={previous}
        next={next}
        count={CRM_SAMPLE_ROWS.length}
        serachable={false}
        categories={[]}
        onRequestData={handleRequestData}
      />
    </div>
  );
}

export default function SharedUiDevPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dialogResult, setDialogResult] = useState("No dialog action yet.");
  const [singleDate, setSingleDate] = useState("2026-04-16");
  const [clearableDate, setClearableDate] = useState("2026-04-18");
  const [rangeDates, setRangeDates] = useState<Date[]>([
    new Date("2026-04-15"),
    new Date("2026-04-19"),
  ]);

  const openDialogDemo = async () => {
    const confirmed = await dialogService.confirm({
      title: "공용 Dialog 확인",
      description: "DialogProvider + CommonDialog + dialogService 흐름을 테스트합니다.",
      confirmLabel: "확인",
      cancelLabel: "취소",
    });
    setDialogResult(confirmed ? "Confirmed through dialogService.confirm()" : "Cancelled through dialogService.confirm()");
  };

  return (
    <Container fluid className="page-content pb-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Developer Surface
                </div>
                <h1 className="mt-2 text-3xl font-semibold text-slate-900">
                  CRM Shared UI Test Page
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-600">
                  Current CRM shared UI surface at <code>/dev/ui-shared</code>. Each section
                  renders a live demo and generated usage metadata from routed pages.
                </p>
              </div>
              <div className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white">
                amazing_crm
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {CRM_SECTION_ORDER.map((sectionId) => {
                const meta = metadataById.get(sectionId);
                return (
                  <Badge key={sectionId} color="soft-secondary">
                    {meta?.label ?? sectionId}
                  </Badge>
                );
              })}
            </div>
          </CardBody>
        </Card>

        {CRM_SECTION_ORDER.map((sectionId) => {
          const meta = metadataById.get(sectionId);
          if (!meta) return null;

          return (
            <Card key={sectionId}>
              <CardHeader>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{meta.label}</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {CRM_SECTION_DESCRIPTIONS[sectionId]}
                    </p>
                  </div>
                  <RouteUsageCountBadge count={meta.usages.length} />
                </div>
              </CardHeader>
              <CardBody className="space-y-6">
                {sectionId === "button" ? (
                  <div className="flex flex-wrap gap-3">
                    {CRM_BUTTON_COLORS.map((color) => (
                      <Button key={color} color={color}>
                        {color}
                      </Button>
                    ))}
                    <Button color="outline-primary">outline-primary</Button>
                  </div>
                ) : null}

                {sectionId === "badge" ? (
                  <div className="flex flex-wrap gap-3">
                    {CRM_BADGE_COLORS.map((color) => (
                      <Badge key={color} color={color} pill>
                        {color}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {sectionId === "card" ? (
                  <Row className="g-3">
                    <Col md={6}>
                      <Card className="h-full">
                        <CardHeader>Summary Card</CardHeader>
                        <CardBody>
                          Shared cards provide the default CRM content container with header,
                          body, and footer sections.
                        </CardBody>
                        <CardFooter>
                          <Button color="soft-primary" size="sm">
                            Footer CTA
                          </Button>
                        </CardFooter>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-full border-dashed">
                        <CardBody>
                          Cards are also used without headers when a compact wrapper is enough.
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                ) : null}

                {sectionId === "modal" ? (
                  <>
                    <Button color="primary" onClick={() => setModalOpen(true)}>
                      Open Shared Modal
                    </Button>
                    <Modal
                      isOpen={modalOpen}
                      onClose={() => setModalOpen(false)}
                      title="Shared Modal Demo"
                      variant="dialog"
                      size="lg"
                    >
                      <ModalBody>
                        The CRM modal demo uses the shared <code>Modal</code> shell and its body,
                        header, and footer primitives.
                      </ModalBody>
                      <ModalFooter>
                        <Button color="light" onClick={() => setModalOpen(false)}>
                          Close
                        </Button>
                        <Button color="primary" onClick={() => setModalOpen(false)}>
                          Confirm
                        </Button>
                      </ModalFooter>
                    </Modal>
                  </>
                ) : null}

                {sectionId === "dialog" ? (
                  <div className="space-y-3">
                    <div className="text-sm text-slate-600">{dialogResult}</div>
                    <Button color="warning" onClick={() => void openDialogDemo()}>
                      Open Shared Dialog
                    </Button>
                  </div>
                ) : null}

                {sectionId === "dropdown" ? (
                  <Dropdown>
                    <DropdownToggle className="btn btn-light">Open Shared Dropdown</DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem header>Quick Actions</DropdownItem>
                      <DropdownItem>Inspect metadata</DropdownItem>
                      <DropdownItem>Open source path</DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem>Close menu</DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                ) : null}

                {sectionId === "form" ? (
                  <Form className="max-w-2xl">
                    <FormGroup>
                      <Label htmlFor="crm-shared-form-name">Display Name</Label>
                      <Input id="crm-shared-form-name" placeholder="Shared UI playground" />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="crm-shared-form-tone">Tone</Label>
                      <Input id="crm-shared-form-tone" type="select" defaultValue="info">
                        <option value="info">Informational</option>
                        <option value="warning">Warning</option>
                        <option value="success">Success</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="crm-shared-form-notes">Notes</Label>
                      <Input
                        id="crm-shared-form-notes"
                        type="textarea"
                        rows={4}
                        placeholder="Shared form fields and feedback demo."
                      />
                      <FormFeedback type="valid">Shared validation feedback example.</FormFeedback>
                    </FormGroup>
                  </Form>
                ) : null}

                {sectionId === "input" ? (
                  <Row className="g-3">
                    <Col md={6}>
                      <Label htmlFor="crm-shared-input-text">Text</Label>
                      <Input id="crm-shared-input-text" placeholder="Shared text input" />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="crm-shared-input-number">Number</Label>
                      <Input id="crm-shared-input-number" type="number" defaultValue={12} />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="crm-shared-input-select">Select</Label>
                      <Input id="crm-shared-input-select" type="select" defaultValue="draft">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="crm-shared-input-check">Checkbox</Label>
                      <div className="pt-2">
                        <Input id="crm-shared-input-check" type="checkbox" defaultChecked />
                      </div>
                    </Col>
                  </Row>
                ) : null}

                {sectionId === "table" ? (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Primitive</th>
                        <th>Purpose</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Table</td>
                        <td>Shared table shell</td>
                        <td>
                          <Badge color="soft-success">Ready</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td>TableHeader / TableRow</td>
                        <td>Rendered through the shared shadcn-backed table primitive</td>
                        <td>
                          <Badge color="soft-primary">Live</Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : null}

                {sectionId === "datepicker" ? (
                  <div className="space-y-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <Label htmlFor="crm-date-single">Single Date</Label>
                        <CommonDatePicker
                          id="crm-date-single"
                          value={singleDate}
                          clearable
                          onChangeValue={setSingleDate}
                        />
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="crm-date-range">Range</Label>
                        <CommonDatePicker
                          id="crm-date-range"
                          value={rangeDates}
                          mode="range"
                          clearable
                          onChangeDates={setRangeDates}
                        />
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="crm-date-clearable">Clearable Date</Label>
                        <CommonDatePicker
                          id="crm-date-clearable"
                          value={clearableDate}
                          clearable
                          onChangeValue={setClearableDate}
                        />
                      </Col>
                    </Row>
                    <Button color="light" onClick={() => setDateModalOpen(true)}>
                      Open Modal DatePicker
                    </Button>
                    <Modal
                      isOpen={dateModalOpen}
                      onClose={() => setDateModalOpen(false)}
                      title="Modal DatePicker Demo"
                      variant="dialog"
                    >
                      <ModalBody>
                        <Label htmlFor="crm-date-modal-picker">Inside shared modal</Label>
                        <CommonDatePicker
                          id="crm-date-modal-picker"
                          value={singleDate}
                          clearable
                          onChangeValue={setSingleDate}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button color="light" onClick={() => setDateModalOpen(false)}>
                          Close
                        </Button>
                      </ModalFooter>
                    </Modal>
                  </div>
                ) : null}

                {sectionId === "paginated-table" ? (
                  <div className="space-y-6">
                    <div>
                      <div className="mb-2 text-sm font-semibold text-slate-900">
                        Client-side Variant: TableContainer
                      </div>
                      <div className="rounded-lg border border-slate-200 p-4">
                        <TableContainer
                          columns={CRM_TABLE_COLUMNS}
                          data={CRM_SAMPLE_ROWS}
                          customPageSize={5}
                          tableClass="table mb-0"
                          theadClass=""
                          trClass=""
                          thClass=""
                          divClass="table-responsive"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-semibold text-slate-900">
                        Server-style Variant: ServerPaginatedTable
                      </div>
                      <MockServerPaginatedTableDemo />
                    </div>
                  </div>
                ) : null}

                <MetadataBlock meta={meta} />
              </CardBody>
            </Card>
          );
        })}
      </div>
    </Container>
  );
}
